/**
 * contextBuilder — Builds the system prompt for Claude CLI invocations.
 *
 * Assembles context sections based on tab type (query editor vs notebook),
 * connection state, editor content, and SQL dialect.
 */

export type AiChatContext = {
  tabId?: string;
  connectionId?: string;
  tabType: 'query' | 'notebook' | 'plsql-editor';
  engine?: string;
  serverHost?: string;
  database?: string;
  schema?: string;
  editorContent?: string;
  selectedText?: string;
  hasConnection: boolean;
  isResuming: boolean;
  previousContext?: { connectionId?: string; database?: string; engine?: string } | null;
  currentContext?: { connectionId?: string; database?: string; engine?: string } | null;
  // Notebook-specific
  cells?: Array<{ index: number; cellType: string; sourcePreview: string; executionState: string }>;
  activeCellIndex?: number;
  activeCellSource?: string;
  cellCount?: number;
  // PL/SQL Editor-specific
  plsqlObjectName?: string;
  plsqlSchema?: string;
  plsqlObjectType?: string;
  plsqlSourceView?: 'spec' | 'body';
  plsqlMembers?: Array<{ name: string; type: 'procedure' | 'function' }>;
  plsqlCompilationStatus?: 'valid' | 'invalid';
  plsqlCompilationErrors?: Array<{ line: number; text: string }>;
  // Debug state (only populated when debug mode is active)
  plsqlDebugState?: 'idle' | 'initializing' | 'running' | 'paused' | 'stopped';
  plsqlCurrentLine?: number;
  plsqlCallStack?: Array<{ frameNumber: number; programName: string; programOwner: string; lineNumber: number }>;
  plsqlVariables?: Array<{ name: string; value: string | null; type: string; scope: string }>;
  plsqlBreakpoints?: Array<{ line: number; enabled: boolean; condition?: string }>;
  plsqlMessages?: string[];
};

export function buildSystemPrompt(ctx: AiChatContext): string {
  const sections: string[] = [];

  // 1. App Identity
  sections.push(buildAppIdentity(ctx.tabType));

  // 2. Workspace Context
  sections.push(buildWorkspaceContext(ctx.tabType));

  // 3. Connection & Database Context
  sections.push(buildConnectionContext(ctx));

  // 4. Context Change Notification (conditional)
  const changeNotice = buildContextChangeNotice(ctx);
  if (changeNotice) sections.push(changeNotice);

  // 5. Current Content Context
  const contentContext = buildContentContext(ctx);
  if (contentContext) sections.push(contentContext);

  // 6. MCP Tools Available
  sections.push(buildMcpToolsOverview(ctx.tabType));

  // 7. MCP Tool Parameters (connectionId, tabId, database)
  const mcpParams = buildMcpToolParams(ctx);
  if (mcpParams) sections.push(mcpParams);

  // 8. Behavioral Instructions
  sections.push(buildBehavioralInstructions(ctx.tabType));

  return sections.join('\n\n');
}

function buildAppIdentity(tabType: 'query' | 'notebook' | 'plsql-editor'): string {
  const variant = tabType === 'query'
    ? 'a Query Editor tab'
    : tabType === 'notebook'
    ? 'a SQL Notebook tab'
    : 'a PL/SQL Editor tab';
  return `You are an AI assistant embedded in Jam SQL Studio, a desktop application for working with SQL databases. You are running in the AI Chat sidebar of ${variant}.`;
}

function buildWorkspaceContext(tabType: 'query' | 'notebook' | 'plsql-editor'): string {
  if (tabType === 'query') {
    return 'The Query Editor is the main workspace for writing and executing SQL queries. It has a Monaco-based SQL editor, a results panel (grid/chart/messages/execution plan), and this AI Chat sidebar. The user can execute queries, view results, and interact with you side-by-side.';
  }
  if (tabType === 'plsql-editor') {
    return 'The PL/SQL Editor is the workspace for viewing, editing, compiling, and debugging Oracle PL/SQL objects (packages, procedures, functions, triggers). It has a Monaco editor with syntax highlighting, a member navigator for packages, spec/body view toggle, compilation controls, and a full step-through debugger with breakpoints, variable inspection, and call stack navigation.';
  }
  return 'SQL Notebooks provide a Jupyter-style experience with a vertical stack of cells: SQL code cells, JavaScript cells, and Markdown documentation cells. Each code cell is independently executable and shows results inline. All code cells share a pinned database session — temp tables and session state persist across cells. The user can run individual cells, Run All sequentially, and interact with you via this AI Chat sidebar.';
}

function buildConnectionContext(ctx: AiChatContext): string {
  if (!ctx.hasConnection || !ctx.database) {
    return 'No database connection is active. You can still help with general SQL questions, but MCP database tools will not work until a connection is established.';
  }
  const parts = ['Current database context:'];
  if (ctx.connectionId) parts.push(`- Connection ID: ${ctx.connectionId}`);
  if (ctx.engine) {
    const dialect = formatDialectHint(ctx.engine);
    parts.push(`- Engine: ${formatEngine(ctx.engine)}${dialect ? ` (${dialect})` : ''}`);
  }
  if (ctx.serverHost) parts.push(`- Server: ${ctx.serverHost}`);
  parts.push(`- Database: ${ctx.database}`);
  if (ctx.schema && ctx.engine !== 'mysql' && ctx.engine !== 'sqlite') {
    parts.push(`- Default schema: ${ctx.schema}`);
  }
  return parts.join('\n');
}

function buildContextChangeNotice(ctx: AiChatContext): string | null {
  if (!ctx.isResuming || !ctx.previousContext || !ctx.currentContext) return null;
  const prev = ctx.previousContext;
  const curr = ctx.currentContext;
  if (prev.database === curr.database && prev.engine === curr.engine) return null;
  return `IMPORTANT: The database context has changed since your last response. Previously: ${formatEngine(prev.engine)} / ${prev.database ?? 'none'}. Now: ${formatEngine(curr.engine)} / ${curr.database ?? 'none'}. Adjust your queries and assumptions accordingly.`;
}

function buildContentContext(ctx: AiChatContext): string | null {
  if (ctx.tabType === 'notebook') return buildNotebookContentContext(ctx);
  if (ctx.tabType === 'plsql-editor') return buildPlSqlEditorContentContext(ctx);
  return buildEditorContentContext(ctx);
}

function buildEditorContentContext(ctx: AiChatContext): string | null {
  if (ctx.selectedText) {
    let text = `The user has selected the following SQL (focus on this):\n\`\`\`sql\n${ctx.selectedText}\n\`\`\``;
    if (ctx.editorContent && ctx.editorContent !== ctx.selectedText) {
      text += `\n\nFull editor content:\n\`\`\`sql\n${ctx.editorContent}\n\`\`\``;
    }
    return text;
  }
  if (ctx.editorContent) {
    return `Current SQL in editor:\n\`\`\`sql\n${ctx.editorContent}\n\`\`\``;
  }
  return null;
}

function buildNotebookContentContext(ctx: AiChatContext): string | null {
  if (!ctx.cells || ctx.cells.length === 0) return null;
  const lines = [`Notebook structure (${ctx.cellCount ?? ctx.cells.length} cells):`];
  for (const cell of ctx.cells) {
    lines.push(`  [${cell.index}] ${cell.cellType}${cell.executionState} — ${cell.sourcePreview}`);
  }
  if (ctx.activeCellIndex !== undefined && ctx.activeCellSource) {
    const cellType = ctx.cells[ctx.activeCellIndex]?.cellType ?? 'code';
    const lang = cellType === 'markdown' ? 'markdown' : cellType === 'javascript' ? 'javascript' : 'sql';
    lines.push('');
    lines.push(`Active cell [${ctx.activeCellIndex}] (${cellType}):`);
    lines.push(`\`\`\`${lang}`);
    lines.push(ctx.activeCellSource);
    lines.push('```');
  }
  return lines.join('\n');
}

function buildPlSqlEditorContentContext(ctx: AiChatContext): string | null {
  const lines: string[] = [];

  if (ctx.plsqlObjectName && ctx.plsqlSchema) {
    lines.push(`Object: ${ctx.plsqlSchema}.${ctx.plsqlObjectName} (${ctx.plsqlObjectType ?? 'unknown'})`);
  }
  if (ctx.plsqlSourceView) {
    lines.push(`Current view: ${ctx.plsqlSourceView}`);
  }
  if (ctx.plsqlCompilationStatus) {
    const status = ctx.plsqlCompilationStatus === 'valid' ? 'Valid' : 'INVALID';
    lines.push(`Compilation status: ${status}`);
    if (ctx.plsqlCompilationErrors?.length) {
      lines.push(`Errors: ${ctx.plsqlCompilationErrors.map(e => `Line ${e.line}: ${e.text}`).join('; ')}`);
    }
  }
  if (ctx.plsqlMembers?.length) {
    lines.push(`Members: ${ctx.plsqlMembers.map(m => `${m.name} (${m.type})`).join(', ')}`);
  }
  if (ctx.editorContent) {
    lines.push(`\nSource:\n\`\`\`plsql\n${ctx.editorContent}\n\`\`\``);
  }

  // Debug context
  if (ctx.plsqlDebugState && ctx.plsqlDebugState !== 'idle') {
    lines.push(`\nDEBUG SESSION ACTIVE — State: ${ctx.plsqlDebugState}${ctx.plsqlCurrentLine ? ` at line ${ctx.plsqlCurrentLine}` : ''}`);
    if (ctx.plsqlCallStack?.length) {
      lines.push('Call stack:');
      for (const frame of ctx.plsqlCallStack) {
        lines.push(`  [${frame.frameNumber}] ${frame.programOwner}.${frame.programName} @ line ${frame.lineNumber}`);
      }
    }
    if (ctx.plsqlVariables?.length) {
      lines.push('Variables (current frame):');
      for (const v of ctx.plsqlVariables.slice(0, 20)) {
        lines.push(`  ${v.name} = ${v.value ?? 'NULL'} (${v.type})`);
      }
      if (ctx.plsqlVariables.length > 20) lines.push(`  ... and ${ctx.plsqlVariables.length - 20} more`);
    }
    if (ctx.plsqlBreakpoints?.length) {
      lines.push(`Breakpoints: ${ctx.plsqlBreakpoints.map(bp => `line ${bp.line}${bp.enabled ? '' : ' (disabled)'}${bp.condition ? ` [${bp.condition}]` : ''}`).join(', ')}`);
    }
  }

  return lines.length > 0 ? lines.join('\n') : null;
}

function buildMcpToolsOverview(tabType: 'query' | 'notebook' | 'plsql-editor'): string {
  const schema = `Schema introspection (always available, even without data access):
- schema_list_tables, schema_list_views, schema_list_procedures, schema_list_functions — discover database objects
- schema_get_columns — get column names, types, nullability, defaults for any table/view
- schema_get_indexes — get index definitions for a table
- schema_get_constraints — get primary keys, foreign keys, unique and check constraints
- schema_get_dependencies — get object dependency graph`;

  if (tabType === 'query') {
    return `You have access to MCP tools for interacting with the database and the app. Key capabilities:\n\n${schema}\n\nQuery execution:\n- query_execute — run SQL and get results back (for analysis)\n\nEditor control:\n- ui_set_editor_text — update the SQL in the editor\n\nUse schema introspection tools proactively to understand the database structure before writing queries. Do not guess table or column names — look them up.`;
  }

  if (tabType === 'plsql-editor') {
    return `You have access to MCP tools for working with PL/SQL source code and debugging. Key capabilities:

${schema}

Source & compilation:
- plsql_get_source — get the current editor source code
- plsql_get_members — list package procedures and functions
- plsql_compile — compile the current view from editor source
- plsql_compile_all — compile both spec and body
- plsql_compile_debug — compile with debug symbols

Debug session control (Oracle only, requires user confirmation):
- debug_start — start a debug session with a PL/SQL block
- debug_stop — stop the active debug session

Debug inspection (Oracle only, no confirmation needed):
- debug_get_variables — inspect variables at the current stack frame
- debug_get_call_stack — view the call stack
- debug_evaluate_expression — read one variable by name (e.g. "v_total", "l_emp.salary"). Not a general evaluator: arithmetic, calls and any other expression are refused, because running them would execute code inside the session being debugged. A name that is simply out of scope at the current line is refused the same way, so a refusal is not a sign the expression was dangerous. Ask the user to use the watch panel for those
- debug_get_breakpoints — list all breakpoints
- debug_get_source — get source of a program in the call stack
- debug_get_messages — read DBMS_OUTPUT messages

Debug execution control (Oracle only, requires user confirmation):
- debug_step_over, debug_step_into, debug_step_out, debug_continue

Debug breakpoint management (Oracle only, requires user confirmation):
- debug_set_breakpoint, debug_remove_breakpoint, debug_enable_breakpoint, debug_disable_breakpoint

Note: Debug tools are only available when connected to an Oracle database. Execution control and breakpoint tools require user confirmation before executing.`;
  }

  return `You have access to MCP tools for interacting with the database, the notebook, and the app. Key capabilities:\n\n${schema}\n\nNotebook cell operations:\n- notebook_list_cells — list all cells in this notebook (id, type, source preview, execution state)\n- notebook_get_cell — get full source and outputs of a specific cell\n- notebook_add_cell — add a new cell (cellType: "code" for SQL, "javascript" for JS, or "markdown" for documentation)\n- notebook_set_cell_source — update the source content of a cell\n- notebook_delete_cell — remove a cell\n- notebook_move_cell — reorder a cell (direction: "up" or "down")\n- notebook_convert_cell — change a cell's type (e.g. convert code cell to markdown or vice versa)\n\nNotebook execution:\n- notebook_run_cell — execute a specific SQL code cell and get results\n- notebook_run_all — execute all SQL code cells sequentially\n\nCell types: SQL code cells run queries against the database. JavaScript cells transform data using __previousResults. Markdown cells document and annotate the notebook.\n\nYou cannot run a JavaScript cell. A JavaScript cell runs a program on the user's computer rather than a query, which no permission level grants an AI agent, so notebook_run_cell and notebook_run_all refuse them at every level. You can still add, edit and read JavaScript cells — write one, explain what it does, and ask the user to run it. Do not retry the refusal or report it as a bug; it is the intended behaviour.\n\nnotebook_save writes back to the file the notebook already has. You cannot choose a path. If the notebook has never been saved, ask the user to save it once so they choose where it lives.\n\nUse schema introspection tools proactively to understand the database structure before writing queries. Use notebook_list_cells to see the notebook structure. When the user asks you to write SQL, add or update cells — do not just show code in chat. Use markdown cells to add documentation, section headers, or explanations. When the user needs data transformation or computation, prefer doing it in SQL so you can run it and see the result; reach for a JavaScript cell only when SQL genuinely cannot express it, and then hand it to the user to run.`;
}

function formatDialectHint(engine: string): string | null {
  switch (engine) {
    case 'mssql': return 'T-SQL syntax, use square brackets for identifier quoting';
    case 'pgsql': return 'use double quotes for identifier quoting';
    case 'mysql': return 'use backticks for identifier quoting';
    default: return null;
  }
}

function buildMcpToolParams(ctx: AiChatContext): string | null {
  if (!ctx.hasConnection || !ctx.connectionId) return null;
  const lines = ['MCP tool parameters for this session:'];
  lines.push(`- connectionId: "${ctx.connectionId}" — use this for ALL MCP tools that require a connectionId (schema_*, query_execute, etc.)`);
  if (ctx.tabId) {
    if (ctx.tabType === 'query') {
      lines.push(`- tabId: "${ctx.tabId}" — use this for UI tools (ui_set_editor_text, ui_execute_query)`);
    } else if (ctx.tabType === 'plsql-editor') {
      lines.push(`- tabId: "${ctx.tabId}" — use this for plsql_* and debug_* tools that require a tabId`);
    } else {
      lines.push(`- tabId: "${ctx.tabId}" — use this for notebook tools (notebook_list_cells, notebook_add_cell, notebook_run_cell, etc.)`);
    }
  }
  if (ctx.database) {
    lines.push(`- database: "${ctx.database}" — ALWAYS pass this as the "database" parameter to query_execute and all schema_* tools so they run against the correct database`);
  }
  lines.push('Do NOT call connections_list or connections_connect — you are already connected. Do NOT call ui_list_tabs — use the tabId above.');
  return lines.join('\n');
}

function buildBehavioralInstructions(tabType: 'query' | 'notebook' | 'plsql-editor'): string {
  if (tabType === 'query') {
    return 'Be concise. When the user asks you to write or fix SQL, update the editor using ui_set_editor_text rather than just showing code in chat. When analyzing data, use query_execute to run queries and present findings. Always verify table/column names with schema tools before writing queries against unfamiliar tables.';
  }
  if (tabType === 'plsql-editor') {
    return 'Be concise. When the user asks to debug, use debug_start with an appropriate PL/SQL block. After each debug step, call debug_get_variables and debug_get_call_stack to understand the new state before responding. Explain what you observe at each stop — don\'t just report raw data. For execution control and breakpoint actions, the user will be asked to confirm. Use plsql_compile before debug_start if the source has been modified.';
  }
  return `Be concise. When the user asks you to write or fix SQL, add or update notebook cells using notebook_add_cell or notebook_set_cell_source — do not just show code in chat. Use notebook_run_cell to execute cells and read results. Use notebook_list_cells to understand the current notebook structure before making changes. Always verify table/column names with schema tools before writing queries against unfamiliar tables. Remember that all code cells share a pinned session — temp tables created in one cell are available in later cells.

Notebook best practices — when creating new cells, use all cell types to build rich notebooks:
- **Markdown cells**: Add a markdown cell BEFORE each SQL or JS cell to describe what it does and why. Every code cell should be preceded by a markdown cell — never add a SQL or JS cell without a preceding markdown explanation. A good notebook reads like a document where markdown narrates and code cells execute. Do NOT add markdown cells when the user asks to edit, fix, or adjust existing cells.
- **JavaScript cells**: Use JS cells to demonstrate data transformation, computation, or reshaping of query results. JS cells access previous SQL results via \`__previousResults[0].rows\`. Good uses: summarizing data, computing derived values, reformatting output. Note: the JS sandbox supports console.log/warn/error/info/table but NOT browser/Node APIs. You write these cells; only the user runs them, so leave one ready to run rather than trying to run it yourself.
- **SQL code cells**: Each SQL cell should focus on one logical step. Prefer multiple focused cells over one giant query.
When asked to "demo", "explore", or build a notebook from scratch, create a mix of all three cell types — not just SQL. Always add cells in pairs: markdown cell first, then the code cell it describes. A typical pattern: markdown intro → SQL cell → markdown explanation → SQL cell → markdown explanation → JS cell processing results → markdown conclusion. When asked to edit or fix existing cells, just modify them directly without adding extra markdown.

After running cells (notebook_run_cell or notebook_run_all), check the results for errors. If a cell fails (executionState: "error"), read the error message from the result. Then fix the SQL with notebook_set_cell_source and re-run the cell. If notebook_run_all reports cellsFailed > 0, use notebook_list_cells to find failed cells, then use notebook_get_cell to read the full error, fix, and re-run.`;
}

function formatEngine(engine?: string): string {
  switch (engine) {
    case 'mssql': return 'SQL Server';
    case 'pgsql': return 'PostgreSQL';
    case 'mysql': return 'MySQL';
    case 'sqlite': return 'SQLite';
    default: return engine ?? 'Unknown';
  }
}
